const challenge_overview_2023 = String.raw`
# ASRU2023 ML-SUPERB Challenge

## Contents

- [Evaluation Framework](/challenge-asru2023/framework): The evaluation framework of ASRU2023 ML-SUPERB challenge.
- [Upstream Specification](/challenge-asru2023/upstream): The Upstream Specification about data, programming language, and interface.

## Abstract

This challenge

* Benchmarks **generalizability** of **Self-Supervised Learning (SSL)** speech model in multilingual scenarios.
    * A new evaluation framework that combines [S3PRL](https://github.com/s3prl/s3prl) and [ESPnet](https://github.com/espnet/espnet)
* Focuses on four tasks
    * Monolingual: ASR
    * Multilingual: ASR, LID, ASR + LID
* Has three tracks
    * ML-SUPERB Track:
        * Two leaderboards for submissions
            * Public-set: Participants will use publicly available datasets and submit their prediction files.
            * Hidden-set: Participants will use data collected specifically for the new-language track and test their models on this unseen data.
    * New-language Track: Participants can submit additional ML-SUPERB-style data to the benchmark, facilitating the evaluation of SSL models on new languages.
    * Research Paper Track: Participants are encouraged to submit research papers that utilize the ML-SUPERB evaluation framework for their experiments and analysis.

## Resources

* [ML-SUPERB evaluation framework](https://github.com/espnet/espnet/tree/master/egs2/ml_superb/asr1)
* ML-SUPERB public-set ([Google Drive](https://drive.google.com/file/d/1zslKQwadZaYWXAmfBCvlos9BVQ9k6PHT/view?usp=sharing) [Huggingface]()(Coming soon))
* List of open-sourced speech SSL
    * [S3PRL List](https://s3prl.github.io/s3prl/tutorial/upstream_collection.html)
* Suggested speech SSL for the New-Language track (the same as the [ML-SUPERB paper](https://arxiv.org/abs/2305.10615)) 
    - wav2vec2-base https://s3prl.github.io/s3prl/tutorial/upstream_collection.html#wav2vec2
    - wav2vec2-large https://s3prl.github.io/s3prl/tutorial/upstream_collection.html#wav2vec2-large-ll60k
    - robust-wav2vec2-large https://s3prl.github.io/s3prl/tutorial/upstream_collection.html#wav2vec2-large-lv60-cv-swbd-fsh
    - wav2vec2-base-23 https://huggingface.co/facebook/wav2vec2-base-100k-voxpopuli
    - wav2vec2-large-23 https://huggingface.co/facebook/wav2vec2-large-100k-voxpopuli
    - XLSR-53 https://s3prl.github.io/s3prl/tutorial/upstream_collection.html#xlsr-53
    - XLSR-128 https://s3prl.github.io/s3prl/tutorial/upstream_collection.html#xls-r-300m
    - HuBERT-base https://s3prl.github.io/s3prl/tutorial/upstream_collection.html#id15
    - Hubert-large https://s3prl.github.io/s3prl/tutorial/upstream_collection.html#hubert-large-ll60k
    - HuBERT-base-cmn https://huggingface.co/TencentGameMate/chinese-hubert-base
    - HuBERT-large-cmn https://huggingface.co/TencentGameMate/chinese-hubert-large
    - mHuBERT-base https://s3prl.github.io/s3prl/tutorial/upstream_collection.html#mhubert-base-vp-en-es-fr-it3

## Track1: ML-SUPERB  Track

### Track Information
In The ML-SUPERB track of the challenge is designed for participants to submit their self-supervised models to the ML-SUPERB benchmark.

We offer two leaderboards for submission:
* **Pubic-set**
    * Brief information: The public dataset is publicly available, and participants are required to submit prediction files for public-benchmark submissions.
    * Evaluation specifics:
        * Fixed downstream model architectures (CTC-based transformer network).
        * Frozen upstream SSL model.
        * No limitations on downstream training except for the above constraints (e.g., hyperparameters, optimizers, etc., are free to choose).
* **Hidden-set**: Data for this set will be collected by the New-Language track and will be used to test the candidate models.
    * The organizer will utilize different learning rates for each downstream task training while keeping all other hyperparameters fixed. The best results will be presented on the leaderboard.

### Importat note
The primary goal of this challenge is to **encourage innovation** and foster a **less competitive** environment.
* Participants have the freedom to choose whether they want their submissions to appear on the leaderboard. By default, submissions will not be displayed on the leaderboard.
* As the focus is on extending the SSL model to multilingual scenarios, **continual pre-training** from existing self-supervised models on speech (or other modalities) is also encouraged.
* In addition to model accuracy, metrics such as the number of parameters and operations will be considered to capture the computation efficiency of the proposed approaches. Algorithmic improvements from diverse perspectives are highly encouraged.

### Submission to Track1
* Participants are required to submit an upstream model to the hidden-set benchmark.
* (Optional but strongly recommended) Participants are encouraged to submit a prediction file to the public-set benchmark. If not submitted, the submitted upstream model for the hidden-set will be used by the organizer to evaluate on the public-set benchmark.
* (Optional) System description paper
    * To verify that the submitted upstream model follows the challenge policy, participants are suggested to submit a system description paper in the ASRU submission format without a page limit. The paper should describe the method used for their submissions and should include the following information at minimum:
        * SSL ojbectives
        * Model architeture
        * Pre-training data
        * Parameter size for each submission.
* * [Submission Form](https://forms.gle/GMnzmSDy2AP8V2Sr8)

## Track2: New-langauge Track

### Track Information
To enhance multilingual research, we have introduced a new-language track that allows participants to submit their own data sources as part of the benchmark.

* What data would be suitable for submission?
    * There are generally no specific requirements for data submissions to this track as long as it is **legally permissible** for use in the challenge.
    * We encourage submissions of data from either different languages, recording conditions, or speech types (e.g., read speech, conversational speech, lecture speech, etc.).
    * Even if the language is already covered by the current ML-SUPERB public-set data, we still encourage submissions if the data has unique properties.

### Two Options for Data Submission
We understand the sensitivity and potential legal issues surrounding some data when sharing it publicly. Therefore, we offer two options for data submission:

* **Internal hidden-set evaluation only**:
    * By selecting this option, the submitted data will be exclusively used by the organizers for the hidden-set evaluation of the challenge and will not be included in future versions of ML-SUPERB as a public data source. If any legal agreements or signatures are required, please contact the organizers directly.
* **Future version of ML-SUPERB**:
    * By selecting this option, the submitted data will not only be used for the hidden-set evaluation of this challenge but also considered for inclusion in future versions of the ML-SUPERB public benchmark.
    * Please note that this option requires data holders' approvals for a free license for derivations and commercial use. We require permission for derivations to use the data in creating the future version of ML-SUPERB's public-set, and commercial permission to encourage participation from industry.

### ML-SUPERB style data
* The file structure are expected to be a zipped file named ${"`"}LanguageSubmission_[YourTeamName].zip${"`"} as:
${"`"}${"`"}${"`"}
- ML_SUPERB
    - [Data_Source_Name1]
        - [Lang_ID1]
            - transcript_10min_dev.txt
            - transcript_10min_test.txt
            - transcript_10min_train.txt
            - transcript_1h_train.txt
            - wav
                - [Data_Source_Name1]_[Lang_ID1]_000001.wav
                - [Data_Source_Name1]_[Lang_ID1]_000002.wav
                - ...
        - [Lang_ID2]
            - transcript_10min_dev.txt
            - transcript_10min_test.txt
            - transcript_10min_train.txt
            - transcript_1h_train.txt
            - wav
                - [Data_Source_Name1]_[Lang_ID2]_000001.wav
                - [Data_Source_Name1]_[Lang_ID2]_000002.wav
                - ...
        - ...
${"`"}${"`"}${"`"}
* The ${"`"}transcript_{10min, 1h}_{train, dev, test}.txt${"`"} should in a format as (split the field by <\t>): 
${"`"}${"`"}${"`"}
[Data_Source_Name]_[Lang_ID]_000001 [Original_File(if have)]   [Transcript]
${"`"}${"`"}${"`"}
For example:
${"`"}${"`"}${"`"}
LAD_eng_000257  rbu_z0001_371   Some of the countries have surveys for multiple years.
${"`"}${"`"}${"`"}
* The audio file should be in
    * 16-bit Signed Integer PCM (WAV) format
    * Single Channel
    * 16000 sampling rate


### Submission to Track2
* The data submission should adhere to the current ML-SUPERB style of data, including:
    * Three 10-minute sets (for train/dev/test) and a 1-hour set (for train). There should be no overlap across different sets.
    * The transcription for train/dev/test should not overlap.
    * We understand that special treatment may be required for certain transcriptions. If there are any specific considerations, please communicate them via email. Otherwise, we will follow the default processing scripts provided in the codebase. For example, for Mandarin and Japanese, we may prefer to use phonemized transcriptions as it may not be possible to cover all symbolic characters in the train set.
    * Instead of directly sending the data to the organizers, we request participants to submit the data with a link for downloading (e.g., huggingface, Zenodo, Google Drive, Dropbox, or a direct URL compatible with wget). Please ensure that the data is accompanied by the appropriate license for its use in this challenge.
* Data description paper
    * The submission to the new-language track requires a data description paper in ASRU format that:
        * Describes the language used in the submitted data.
        * Provides details about how the data was collected.
        * Conducts an investigation of the submitted data in the ML-SUPERB monolingual ASR task by testing suggested (or more) SSL models from the resources list.
* [Submission Form](https://forms.gle/Vcd3XJLz1cZ2zNMy6)

## Track3: Research Paper Track

### Track Information
The Research Paper Track is dedicated to accepting research papers that utilize the ML-SUPERB evaluation framework.

### Submission to Track3
Participants are required to submit a research paper following the guidelines specified by ASRU. The submission should demonstrate the use of the ML-SUPERB evaluation framework within the paper.

## Organizers

Shinji Watanabe (CMU)

Jiatong Shi (CMU)

William Chen (CMU)

Dan Berrebbi (CMU)

Hung-yi Lee (NTU)

Shang-Wen Li (Meta)

Abdelrahman Mohamed (Rembrand)

## Hidden-set Committee

Jiatong Shi

William Chen

Dan Berrebbi

# Contact

[superb.announcement@gmail.com](mailto:superb.announcement@gmail.com)
`

const framework = String.raw`
## Evaluation Framework

Please check https://github.com/espnet/espnet/tree/master/egs2/ml_superb/asr1#readme for details.

`

const upstream = String.raw`
## Upstream Specification

### Unlabeled data only: Focus on SSL

- Any labeled/parallel data made by human annotators are **not allowed** to used for both model training and data preprocessing, e.g.
    - **audio/text pairs:** transcriptions in English, foreign languages, or phonemes.
    - **audio/tagging pairs:** speaker labels or sound event labels.
    - **audio/audio pairs:** audios with the same properties made parallel by human, e.g. audios with same content from different speakers, or the opposite.
- Any system pre-trained by labeled/parallel data **cannot** be used to help with the SSL pre-training, like pre-trained ASR.
- Any unlabeled/unparallel data is allowed, including the downstream datasets in the public-set. The nature alignments (not made by human annotators) between audio and other modalities are also allowed, e.g. videos.
- If it is hard to define whether your data is labeled/parallel, please [contact us](/challenge-slt2022/challenge_overview#Contact)!

### Programming Language

- We currently support:
    - **Python >= 3.6**
    - **Pytorch >= 1.7**

- We expect the upstream submission can pass the following check:
    ~~~python=
    upstream = YourModel.cuda()
    assert isinstance(upstream, torch.nn.Module)
    ~~~


We accept upstream models in PyTorch by default. If you wish to submit upstreams in non-PyTorch frameworks, or are not feasible to submit the pre-trained model, please [contact us](/challenge-slt2022/challenge_overview#Contact) for us to see how to help!

### Interface functions

See [here](https://huggingface.co/superb/superb-submission#1-add-model-interfaces).
`

const submission = String.raw`
## Public-set

### As the task definition and demonstration

The public-set serves as the demonstration of the task design, including:
* the data preprocessing,
* tasks' input/output formats,
* task-specific metrics. 

The datasets used in the public-set are all chosen to be public available for everyone to participate. Please refer to [TASKS](/tasks) and the implementation in [S3PRL](https://github.com/s3prl/s3prl/blob/master/s3prl/downstream/docs/superb.md) for details.

### Provide baselines for comparison

#### Baselines

We collected most of the well-known SSL baseline models in [S3PRL](https://github.com/s3prl/s3prl/blob/master/s3prl/downstream/docs/superb.md), including TERA, wav2vec 2.0, HuBERT, DeCoAR 2.0, WavLM, LightHuBERT, and more. You can easily benchmark different upstreams by specifying in the command line arguments.

#### Comparison

Since the full benchmarking on the public-set can take some time for the training to converge. We released the [training artifacts](https://github.com/s3prl/s3prl/blob/master/s3prl/downstream/docs/superb_artifacts.md) of the top baseline systems (e.g. wav2vec2, HuBERT, WavLM) for participants to quickly compare with them. The artifacts include:

- Tensorboard logs
- Trained downstream weights (the best on public dev set)

### Public-set leaderboard and submission

The [public-set leaderboard](/leaderboard?subset=Public+Set&track=constrained) is online and [accepts submissions](/submit?type=public) (need login). There is no deadline for it. Since all the train/dev/test splits are public available, **the leaderboard accepts submissions with the inferenced prediction files on each task's testing split** which will be auto-generated if you follow the benchmarking steps in [S3PRL](https://github.com/s3prl/s3prl/blob/master/s3prl/downstream/docs/superb.md). And in this challenge, we only consider submissions submited to the **Constrained track** (see [Rules](/rules) for further information about track).

## Hidden-set

We use Hugging Face as the platform for model submission, and here is the [submit tutorial]((https://huggingface.co/superb/superb-submission)).

### Fairness & prevent overfitting

Since all the train/dev/test splits are public in the public-set, it is possible to cheat by directly reporting the best results on the testing split, and the results are thus overfit on the testing split. Hence, the hidden-set is collected and prepared to follow the same task design as that in the public-set but with the newly created data. All the splits will **NOT be released in both audio and labels**. The members involved in the hidden-set preparation should **NOT** participate the challenge. These members are listed in the [**Hidden-set Committee**](/challenge-slt2022/challenge_overview#Hidden-set-Committee) below.

### Hidden-set leaderboard and submission

#### Submission type

The leaderboard accepts **submissions with the upstream model solely**, including **model definition** and **pre-trained weights**. The upstream model should follow the specification detailed at [Upstream Specification](/challenge-slt2022/upstream). The submission can be done publicly or privately. Only the [**Hidden-set Committee**](/challenge-slt2022/challenge_overview#Hidden-set-Committee) members can access the privately submitted upstreams and the models will be used solely for this challenge.

- We accept upstream models in PyTorch by default. If you wish to submit upstreams in non-PyTorch frameworks, please [contact us](/challenge-slt2022/challenge_overview#Contact)!
- If you are not feasible to submit the pre-trained model, please [contact us](/challenge-slt2022/challenge_overview#Contact) for us to see how to help!

#### Finetuning on submission

After the upstream model is submitted, we **benchmark the submitted upstream by finetuning each task's downstream model for participants**. **The quota for submissions per week is limited and starts from 2 times/week,** but will be dynamically adjusted based on the number of participants. The quota adjustment will be announced at [NEWS](https://superbbenchmark.org/news). Participants can [contact us](/challenge-slt2022/challenge_overview#Contact) to acquire the finetuning artifacts of their own submissions for sanity checks, including:

- Tensorboard logs
- Testing results
- Trained downstream weights

#### Private dev/test scores

After training the downstream model for all tasks with several different learning rates, we will show the best performance of each task on the hidden-set's development splits. And show the true performance from the same checkpoint on the testing splits.

#### How to submit

The [hidden-set leaderboard](/leaderboard?subset=Hidden+Dev+Set&track=constrained) is online and accepts submissions. Please follow the submission steps. We use HuggingFace's Hub to host the submitted upstreams and track the submitted model weights. On the other hand, we use our submission page to control the submission limit, where the participants tell us the locations of their models on HuggingFace's Hub.

After submitting via the submission page, you will see a new entry in the submission history under your Profile page, and the evaluation results on the hidden-set will be revealed at the entry within one week. Please stay tuned!

All the submitted results will remain anonymous during and after the challenge. You can select a few submissions to show on the leaderboard. If no submission is selected, we will show all your submissions on the leaderboard anonymously. Only you can see your personal/model information on the leaderboard, but you will not see others'. If you wish to reveal your personal/model information on the hidden-set leaderboard, please contact us!

## Profiling Tool

(Update 04/28/2022 AOE)

[The profiling tool](https://github.com/B06901052/DeepSpeed/tree/superb-challenge) we used are mainly based on [**DeepSpeed**](https://github.com/microsoft/DeepSpeed/tree/master/deepspeed/profiling/flops_profiler) developed by **Microsoft**. And for our demands, we fork it and add some additional features. You can use it to profile your model or upstreams in s3prl. All the details are in the repo's README.md.

To match reality, we choose 32 audios as model inputs from LibriSpeech test-clean split by:
1. sort all audios by sequence length
2. choose (82*i + 1)th from i=0~31 audios (2620 audios in total)
`

const metrics = String.raw`
## Overall Metrics

(Update 04/09/2022 AOE)

In this challenge, there are two types of metrics, performance and efficiency metrics.

For performance metrics, we announce two kinds of metrics to measure it: **superb-rank** ($superb_r$) and **superb-score** ($superb_s$) in parameter-agnostic manner. In this challenge, $superb_r$ is the primary metrics. When equal rank is found on two different upstreams, $superb_{s}$ is used to break the tie.

To encourage the development of speech SSL on small and green models, we add two metrics to measure model efficiency, **Params** for memory usage and **MACs** for computataional cost as another targets to make improvement. Also, to gauge the effectiveness and parameter-efficiency of new SSL algorithms, we encourage participants to submit multiple upstreams trained by the same algorithm with the only difference in parameter size. They are computed by [this profiling tool](/challenge-slt2022/submission#Profiling-Tool).

### Notation

Suppose each task $t$ in all tasks $T$ has a single metric $s_t$, and the score of an upstream $u$ on task $t$ is $s_t(u)$ which has already been transformed to make higher values represent better performance. Eg. We use WAcc here for ASR instead of the raw WER.

If there are two or more metrics in one task $T$, such as pesq and stoi in SE, then the $s_t$ will be the arithmetic mean of them.

### Performance Metrics

Performance metrics demonstrate the best performance SSL can achieve, and encourages participants to explore any possibility to push the limits.

#### SUPERB$_s$

To aggregate all task-specific scores $s_t(u)$ into a single static score, we linearly transform each of them into points so that:

- $s_t(fbank) = 0$, The performance of FBANK maps to 0
- $s_t(sota) = 1000$, The performance of the existing SOTA upstream *for this task* maps to 1000.

$$
p_t(u) = \dfrac{1000}{s_t({sota}) - s_t({fbank})}\ (\ s_t(u) - s_t({fbank})\ )
$$

Hence, most of the points will sit between 0~1000. The upstream worse than FBANK on this task will get negative points. The upstream better than the task-specific SOTA upstream will get points higher than 1000. The $superb_s$ of the upstream $u$ is the average of $p_t$ over all tasks.

$$
\text{SUPERB}_s = \dfrac{1}{|T|} \sum_{t \in T}\ p_t(u)
$$

Intuitively, two reference points: FBANK and SOTA decide the typical interested interval for a task-specific metric and scale the task scores to 0~1000 points accordingly. The similar range of points across tasks can then be averaged. Beyond scaling with the pre-defined metric range, this interval further determines *how hard for a task to improve*. For a harder task, its smaller interval at the denominator give the task more credit for any unit improvement.


### Efficiency Metrics

#### MACs (number of Multiply-ACcumulate operations)

For this metrics, we are focus on the algorithm-level, rather than software-level or hardware-level, so the cost yeild from the real implementation will not be taken into consideration. Take the torch.Tensor.\_\_rdiv\_\_ as an example, if its input is int or float, it will call reciprocal, \_\_mul\_\_, and \_\_truediv\_\_, respectively, but we still count it only once. Also, when a function is counted, those functions called in it will not be counted again.

##### Estimated Rule

The original defination of MACs should be a multiply operation followed by an add operation, as below:

$MACs=num(a\times b + c)$

But in the implementation of the profiling tool we used (most done by DeepSpeed, Microsoft), the value may be approximated by following formulas or something similar:

$\approx\max\{num(add)+num(sub), num(mul)+num(div)\}$

$\approx FLOPs/2$

##### Ignored Modules

For the modules below, it will not be taken into account and will raise a warning message when your model forwards a function in them.

- torch.special
- torch.fft
- torch.linalg
- torchaudio
- torchvision

In this challenge, we consider not including those modules since they are usually used as preprocessing (fft, torchaudio) or rarely used (special, linalg). For preprocessing, it maybe has been done by other modules or simply loads the preprocessed data, so we think it will be much fairer if we exclude those computation. And for those modules rarely used, we ignore them due to the time cost. If you think we should add some functions in them into account, feel free to [contact us](/challenge-slt2022/challenge_overview#Contact) or PR your formulas for those functions to [the profiling tool](https://github.com/B06901052/DeepSpeed/tree/superb-challenge). 

#### Params (number of Parameters)

- The total number of parameters in your upstream model using in any downstream task.

### Conclusion

The ranking metrics are the primary performance measures in this challenge and are designed to emphasize an upstream's universally usability. When two upstreams tie on the same rank, the scoring metrics take the tasks' variations and improvement difficulty into account to help the final decision. You can refer to the [public-set leaderboard](/leaderboard?subset=Public+Set&track=constrained) for the overall metrics calculation.
`

export { challenge_overview_2023, framework, upstream, submission, metrics }
// using String.raw to avoid escaping in latex expression
// to meet the parsing format, do things as below
// remove comment <!-- ...  -->
// replace ``` with ~~~ (code block)
// replace <strong> with ** 
// replace math block below $superb_{sp}$ as another ver. (origin ver. can't be render but the reason is unknown)
